import * as searchlightActions from 'a/actions/searchlightActions';

import { getCurrentChapterName, getBookAttempts, getBookStatus } from '../selectors';
import { GAME_ID } from '../../data/constants';
import READING_MODES from '../../data/reading_modes';
import BOOK_STATUS from '../../data/book_status';
import {
  getIsQuestWorkflow,
  getBookId,
  getChapterId,
  getReadingMode,
  getActivityIndex,
  getQuestionCount,
  getQuestionsCorrect,
  getSettings,
  getSavedRoundSessionId,
  getBookSkillId,
  getBookLevel,
} from '../selectors/reporting';

// OVERALL BOOK FLOW

/**
 * send a standard Searchlight event for LOAD_GAME when the entire eReader first
 * loads for playing a book. if the user exits the eReader and re-enters, this
 * event will be sent again.
 */
export const reportLoadGame = () => (dispatch, getState) => {
  const state = getState();

  const bookId = getBookId(state);
  const isQuestWorkflow = getIsQuestWorkflow(state);

  dispatch(searchlightActions.reportLoadGame(GAME_ID, { isQuestWorkflow, bookId }));
};

/**
 * send a standard Searchlight event for ROUND_START when the book loads for a
 * session in the eReader. this event used to be called START_BOOK, but now it
 * is standardized to match the reporting for all of the games/activities
 * (e.g. Wordbots).
 * @param {number} [chapterId=null]
 */
export const reportStartBook = (chapterId = null) => (dispatch, getState) => {
  const state = getState();
  const roundSessionId = getSavedRoundSessionId(state);

  const details = {
    /* eReader does not have a level but this property is required by Searchlight,
      level should be the chapter number (for chapter book) or 1 (for non chapter book)
    */
    level: getBookLevel(state),
    skillId: getBookSkillId(state),
    bookId: getBookId(state),
    status: BOOK_STATUS.IN_PROGRESS,
    chapterId: chapterId || getChapterId(state),
    readingMode: getReadingMode(state),
    isQuestWorkflow: getIsQuestWorkflow(state),
    isIntermediateSave: !!roundSessionId,
  };

  dispatch(searchlightActions.reportRoundStart(GAME_ID, details, undefined, roundSessionId));
};

/**
 * send a standard Searchlight event for ROUND_COMPLETE when the user exits the
 * book. if the user exits via the BackButton, that's considered an "early" exit
 * because they didn't yet finish the quest they were assigned in the book. this
 * event used to be called EXIT_BOOK, but now it is standardized to match the
 * reporting for all of the games/activities (e.g. Wordbots).
 * @param {boolean} [isEarlyExit=false] did the user exit the book via the BackButton? defaults false
 * @param {boolean} [isSuccess=false] did the user achieve mastery of this book? defaults false
 */
export const reportExitBook =
  (isEarlyExit = false, isSuccess = false) =>
    (dispatch, getState) => {
      const state = getState();

      dispatch(
        searchlightActions.reportRoundComplete(GAME_ID, {
          level: getBookLevel(state),
          skillId: getBookSkillId(state),
          bookId: getBookId(state),
          status: getBookStatus(state),
          bookAttempts: getBookAttempts(state),
          chapterId: getChapterId(state),
          readingMode: getReadingMode(state),
          questionCount: getQuestionCount(state),
          questionsCorrect: getQuestionsCorrect(state),
          isSuccess: isEarlyExit ? undefined : isSuccess,
          isEarlyExit,
          isQuestWorkflow: getIsQuestWorkflow(state),
          isIntermediateSave: getIsQuestWorkflow(state) && isEarlyExit,
        }),
      );
    };

/**
 * send a standard Searchlight event for LEVEL_COMPLETE when the user finishes
 * the book or assigned chapter. this event used to be called EXIT_BOOK, but now
 * it is standardized to match the reporting for all of the games/activities
 * (e.g. Wordbots).
 * @param {boolean} [isSuccess=false] did the user achieve mastery?
 */
export const reportLevelComplete = (isSuccess = false) => (dispatch, getState) => {
  const state = getState();

  dispatch(searchlightActions.reportLevelComplete(GAME_ID, {
    level: getBookLevel(state),
    bookId: getBookId(state),
    bookAttempts: getBookAttempts(state),
    chapterId: getChapterId(state),
    isSuccess,
    isQuestWorkflow: getIsQuestWorkflow(state),
  }));
};

// BOOK NAVIGATION

/**
 * send a Searchlight event for PAGE_TURN when the user navigates between pages
 * of an eReader book. this event helps track how long a user spent reading each
 * page.
 * @param {boolean} isPageForward is the user progressing through the book?
 */
export const reportPageTurn = isPageForward => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportPageTurn(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      readingMode: getReadingMode(state),
      pageForward: isPageForward,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a Searchlight event for OPEN_TOC when the user opens the table of
 * contents for a chapter book.
 */
export const reportOpenTableOfContents = () => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportOpenTableOfContents(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a Searchlight event for NAV_TOC when the table of contents is used to
 * navigate through chapters of a book.
 * @param {string} chapter identifier for the chapter being navigated to
 */
export const reportNavigateTableOfContents = chapter => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportNavigateTableOfContents(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      chapter,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a Searchlight event for CLOSE_TOC when the user closes the table of
 * contents for a chapter book.
 * @param {string} closeMethod which behavior the user did to close the table of contents ("toc_icon", "text_area", or "nav")
 */
export const reportCloseTableOfContents = closeMethod => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportCloseTableOfContents(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      closeMethod,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

// READING MODES

/**
 * send a Searchlight event for START_READING_MODE when a user begins a type of
 * reading (e.g. "clean reading").
 * @param {string} readingMode reading mode that is being started
 */
export const reportStartReadingMode = readingMode => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportStartReadingMode(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      readingMode: readingMode === READING_MODES.CLEAN ? 'clean_reading' : 'activity_reading',
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a Searchlight event for EXIT_READING_MODE when a user exits a type of
 * reading (e.g. "clean reading").
 * @param {string} readingMode reading mode that is being exited
 */
export const reportExitReadingMode = readingMode => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportExitReadingMode(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      readingMode: readingMode === READING_MODES.CLEAN ? 'clean_reading' : 'activity_reading',
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a Searchlight event for START_CHAPTER when a user beings a chapter, even
 * when they are navigating backwards to and through a previous chapter.
 * @param {string} chapterId identifier for the chapter
 */
export const reportStartChapter = chapterId => (dispatch, getState) => {
  const state = getState();
  const isQuestWorkflow = getIsQuestWorkflow(state);

  dispatch(
    searchlightActions.reportStartChapter(GAME_ID, {
      bookId: getBookId(state),
      chapterId,
      targetChapter: isQuestWorkflow ? getCurrentChapterName(state) : undefined,
      isQuestWorkflow,
    }),
  );
};

/**
 * send a Searchlight event for END_CHAPTER when a user ends a chapter, even
 * when they are navigating to and through a previous chapter.
 * @param {string} chapterId identifier for the chapter
 */
export const reportExitChapter = chapterId => (dispatch, getState) => {
  const state = getState();
  const isQuestWorkflow = getIsQuestWorkflow(state);

  dispatch(
    searchlightActions.reportExitChapter(GAME_ID, {
      bookId: getBookId(state),
      chapterId,
      targetChapter: isQuestWorkflow ? getCurrentChapterName(state) : undefined,
      isQuestWorkflow,
    }),
  );
};

// SCAFFOLDING

/**
 * send a standard Searchlight event for RW_OPEN when a user clicks to reveal
 * the definition for a reveal word.
 * @param {string} revealWordId identifier for the reveal word
 */
export const reportOpenRevealWord = revealWordId => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportRWOpen(GAME_ID, {
      level: getBookLevel(state),
      // eReader does not have meaningful questionIds for reveal words but this
      // property is required for Searchlight
      questionId: getBookId(state),
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      readingMode: getReadingMode(state),
      revealWordId,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a standard Searchlight event for RW_CLOSE when a user clicks the close
 * button for the popup OR when the user clicks outside the popup.
 * @param {string} revealWordId identifier for the reveal word
 * @param {boolean} [clickedOutside=false] true if the user closed the popup by clicking outside its boundaries
 */
export const reportCloseRevealWord =
  (revealWordId, clickedOutside = false) =>
    (dispatch, getState) => {
      const state = getState();
      dispatch(
        searchlightActions.reportRWClose(GAME_ID, {
          level: getBookLevel(state),
          // eReader does not have meaningful questionIds for reveal words but this
          // property is required for Searchlight
          questionId: getBookId(state),
          bookId: getBookId(state),
          chapterId: getChapterId(state),
          readingMode: getReadingMode(state),
          revealWordId,
          clickedOutside,
          isQuestWorkflow: getIsQuestWorkflow(state),
        }),
      );
    };

/**
 * send a standard Searchlight event for RW_AUDIO_BUTTON when a user clicks a
 * speaker icon to hear either the reveal word or its definition read aloud.
 * @param {string} revealWordId identifier for the reveal word
 * @param {string} audioType either 'word' or 'definition', depending on type of read aloud
 */
export const reportReadRevealWord = (revealWordId, audioType) => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportRWAudioButton(GAME_ID, {
      level: getBookLevel(state),
      // eReader does not have meaningful questionIds for reveal words but this
      // property is required for Searchlight
      questionId: getBookId(state),
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      readingMode: getReadingMode(state),
      revealWordId,
      audioType,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a standard Searchlight event for READ_ALOUD when a user clicks a
 * speaker icon to have text read to them from inside a book or activity.
 * @param {string} targetText text that was read aloud to the user
 */
export const reportReadAloud =
  (targetText = '') =>
    (dispatch, getState) => {
      const state = getState();
      dispatch(
        searchlightActions.reportReadAloud(GAME_ID, {
          // eReader does not have meaningful questionIds for most read alouds but
          // this property is required for Searchlight
          questionId: getBookId(state),
          bookId: getBookId(state),
          chapterId: getChapterId(state),
          readingMode: getReadingMode(state),
          targetText,
          isQuestWorkflow: getIsQuestWorkflow(state),
        }),
      );
    };

// ACTIVITIES

/**
 * send a Searchlight event for START_ACTIVITY when the user activates an
 * activity popup.
 * @param {object} activity the data for the activity
 * @param {string} activity.activityType the name of the activity type
 * @param {string} activity.questionId a unique identifier for this question
 * @param {number} activity.questionIndex the order of this question
 * @param {number} activity.attemptNumber the number of times a user has attempted to answer this question, including this attempt
 */
export const reportStartActivity = ({
  activityType,
  questionId,
  attemptNumber,
}) => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportStartActivity(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      activityType,
      questionId: questionId.toString(),
      // questionNumber is zero-indexed for readability
      questionNumber: getActivityIndex(state, { activityType, questionId }) + 1,
      attemptNumber,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

/**
 * send a standard Searchlight event for SELECTION when the user selects an
 * answer to an activity. this event used to be called ACTIVITY_RESPONSE, but
 * now it is standardized to match the reporting for all of the
 * games/activities (e.g. Wordbots).
 * @param {object} selection the unique ID for this activity
 * @param {string} selection.activityType the kind of activity (Hangry Goblins, etc.)
 * @param {string} selection.questionId the unique ID for this activity
 * @param {number} selection.questionIndex the order of this question
 * @param {array} selection.possibleAnswers array of all possible answers for this activity
 * @param {string} selection.answerChosen the user's answer to the activity
 * @param {string} selection.correctAnswerId the unique identifier for the correct answer
 * @param {boolean} selection.isCorrect does the answer match the target answer?
 * @param {number} selection.attemptNumber how many times the user has attempted to answer, including this attempt
 */
export const reportSelection =
  ({
    activityType,
    questionId,
    possibleAnswers,
    answerChosen,
    correctAnswerId,
    isCorrect,
    attemptNumber,
  }) =>
    (dispatch, getState) => {
      const state = getState();
      dispatch(
        searchlightActions.reportSelection(GAME_ID, {
          level: getBookLevel(state),
          bookId: getBookId(state),
          bookAttempts: getBookAttempts(state),
          chapterId: getChapterId(state),
          activityType,
          // what order was this question answered in?
          questionIndex: getActivityIndex(state, { activityType, questionId }) + 1,
          questionId: questionId.toString(),
          // how many times has the user attempted this question?
          attemptNumber,
          // which answer was chosen?
          action: answerChosen,
          // which answer is correct?
          targetAction: correctAnswerId,
          // what were all the possible answers?
          possibleActions: possibleAnswers,
          isCorrect,
          isQuestWorkflow: getIsQuestWorkflow(state),
        }),
      );
    };

/**
 * send a Searchlight event for EXIT_ACTIVITY when a user finishes an activity
 * popup or chooses to leave that popup early.
 * @param {object} activity the data for the activity
 * @param {string} activity.activityType the name of the activity type
 * @param {string} activity.questionId a unique identifier for this question
 * @param {number} activity.questionIndex the order of this question
 * @param {number} activity.attemptNumber the number of times a user has attempted to answer this question, including this attempt
 */
export const reportExitActivity = ({
  activityType,
  questionId,
  attemptNumber,
}) => (dispatch, getState) => {
  const state = getState();
  dispatch(
    searchlightActions.reportExitActivity(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      activityType,
      questionId: questionId.toString(),
      // questionNumber is zero-indexed for readability
      questionNumber: getActivityIndex(state, { activityType, questionId }) + 1,
      attemptNumber,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};

// SETTINGS

/**
 * send a standard Searchlight event for SETTING when a user changes the font
 * size, paragraph numbering, etc. options in the eReader.
 * @param {object} setting settings object for the eReader
 * @param {number} setting.fontSize actual font size value
 * @param {boolean} setting.showSentenceNumbers should lines and paragraphs be numbered?
 * @param {boolean} setting.showReadForMe should speaker icons and read aloud be shown?
 * @param {boolean} setting.showRevealWords should reveal words be shown?
 */
export const reportChangeSettings = setting => (dispatch, getState) => {
  const state = getState();
  const oldSettings = getSettings(getState());
  dispatch(
    searchlightActions.reportReaderChangeSettings(GAME_ID, {
      bookId: getBookId(state),
      chapterId: getChapterId(state),
      ...oldSettings,
      ...setting,
      isQuestWorkflow: getIsQuestWorkflow(state),
    }),
  );
};
